import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline, MdOutlineGroupAdd, MdSearch } from "react-icons/md";
import Select from "react-select";

import empty_page from "../../../assets/selection_placeholder.svg";
import Button from "../../../components/Button";
import CustomDataTable from "../../../components/CustomDataTable";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AssignMultiStudentsModal from "../../../components/modals/common/AssignMultiStudentsModal";
import AddStudentModal from "../../../components/modals/school/AddStudentModal";
import { USER_TYPE } from "../../../constants/common";
import { useTheme } from "../../../context/ThemeContext";
import useAuth from "../../../hooks/useAuth";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import teacherServices from "../../../services/teacherServices";
import { selectStylesV1 } from "../../../styles/selectStyles";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const TeacherStudents = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigateWithLang();
  const lang = getLanguage();
  const user = useAuth();

  // State management
  const [loading, setLoading] = useState(false); // Fixed typo from `laoding` to `loading`
  const [searchText, setSearchText] = useState("");
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);

  const addStudentModalRef = useRef();
  const addMultiStudentsModalRef = useRef();

  // Open modals
  const openAddStudentModal = (classroom) => addStudentModalRef.current.openModal(classroom);
  const openMultiStudentsModal = (classroom) => addMultiStudentsModalRef.current.openModal(classroom);

  const handleAssignStudentsSuccess = () => {
    handleSelectClassroom(selectedClassroom);
  };
  const handleAddStudentsSuccess = () => {
    handleSelectClassroom(selectedClassroom);
  };

  const fetchClassrooms = useCallback(async () => {
    setLoadingClassrooms(true);
    try {
      const res = await teacherServices.getClassroomsByTeacher(null);

      const formattedClassrooms = res.data.map((classroom) => ({
        ...classroom,
        label: classroom.name + " - " + classroom.section,
        value: classroom.id,
      }));
      setClassrooms(formattedClassrooms);
      // Restore selected classroom if available in local storage
      const savedClassroomId = localStorage.getItem("selectedClassroomId");

      if (savedClassroomId) {
        const selected = formattedClassrooms.find((classroom) => classroom.value == savedClassroomId);
        setSelectedClassroom(selected || null);
        handleSelectClassroom(selected);
      } else {
        setLoadingClassrooms(false);
      }
    } catch (error) {
      console.error("Error fetching classrooms:", error);
    } finally {
      setLoadingClassrooms(false);
    }
  }, []);

  const fetchStudents = async (classroomId, options = {}) => {
    if (!classroomId) return;
    setLoading(true);
    try {
      const res = await classroomServices.getStudentsByClassroomID(classroomId, options);
      setStudents(res.data);
      setFilteredStudents(res.data);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassrooms();
  }, [lang]);

  useEffect(() => {
    return () => {
      localStorage.removeItem("selectedTeacherId");
      localStorage.removeItem("selectedTeacherName");
      localStorage.removeItem("selectedClassroomId");
    };
  }, []);

  // Handle classroom selection
  const handleSelectClassroom = (option) => {
    setSelectedClassroom(option);
    if (option) {
      fetchStudents(option.value, { search: searchText, per_page: 10, order_dir: "asc" });
    } else {
      setStudents([]);
    }
  };
  const normalizeText = (text) => {
    return text.trim().replace(/\s+/g, " ").toLowerCase();
  };
  const handleSearch = (event) => {
    const rawInput = event.target.value;
    setSearchText(rawInput);

    const normalizedQuery = normalizeText(rawInput);

    const filtered = students.filter((student) =>
      normalizeText(student.student.name).includes(normalizedQuery)
    );

    setFilteredStudents(filtered);
  };

  const navigateStudentDetails = (id) => {
    navigate(`/${USER_TYPE.TEACHER.ROLE}/students/${selectedClassroom?.id}/${id}`);
  };

  const columns = [
    {
      name: t("student"),
      field: "student.name", // Use a string reference for simple rendering
      sortable: true,
      body: (rowData) => (
        <Button
          tooltip={rowData.student.name}
          labelStyle="block w-44 overflow-hidden truncate whitespace-nowrap text-start"
          color="blue"
          variant="link"
          label={rowData.student.name}
          onClick={() => navigateStudentDetails(rowData.student.id)}
        />
      ),
    },
    {
      name: t("completed_lessons"),
      field: "completed_lessons",
      sortable: true,
    },
    {
      name: t("total_points"),
      field: "student.total_points",
      sortable: true,
    },
  ];

  return (
    <>
      <div className="col-span-12 mb-3 grid grid-cols-1 gap-4 md:grid-cols-12">
        <div
          className={`col-span-12 flex w-full items-center rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface md:col-span-12 lg:col-span-4`}
        >
          <Select
            loadingMessage={() => <p>{t("loading")}</p>}
            noOptionsMessage={() => <p>{t("no_options")}</p>}
            className="w-full"
            isClearable
            options={classrooms}
            isLoading={loadingClassrooms}
            value={selectedClassroom}
            onChange={handleSelectClassroom}
            placeholder={t("select_classroom")}
            styles={selectStylesV1(theme)}
          />
        </div>
        {selectedClassroom && (
          <div className="col-span-12 flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface md:col-span-12 lg:col-span-8">
            <h3 title={selectedClassroom?.course?.course} className="text-lg font-semibold text-blue-600">
              {selectedClassroom?.course?.course}
            </h3>
            <p className="text-lg font-semibold text-mutedText dark:text-dark-mutedText">
              {t("students")}: <span className="text-blue-400">{students?.length}</span>
            </p>
          </div>
        )}
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {!selectedClassroom && (
            <div className="flex h-96 flex-col items-center justify-center space-y-9">
              <img height={"22%"} width={"22%"} className="opacity-75" src={empty_page} alt="" />
              <p className="text-lg font-medium text-gray-200">{t("select_classroom_to_view_students")}</p>
            </div>
          )}
          {selectedClassroom && (
            <div className="col-span-12 rounded-xl bg-white shadow-md dark:bg-dark-surface">
              <CustomDataTable
                loading={loading}
                columns={columns}
                data={filteredStudents}
                withPagination
                withSort
                search={
                  <div className="relative w-1/3">
                    <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-mutedText dark:text-dark-mutedText rtl:left-auto rtl:right-3" />
                    <input
                      type="text"
                      placeholder={t("search")}
                      value={searchText}
                      onChange={handleSearch}
                      className="w-full rounded-md border bg-white py-3 pl-10 pr-3 text-sm placeholder:text-mutedText focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white rtl:pl-3 rtl:pr-10"
                    />
                  </div>
                }
                actions={
                  <>
                    <Button
                      iconLeft={<MdOutlineGroupAdd size={22} />}
                      label={t("assign_students")}
                      disabled={!selectedClassroom}
                      onClick={() => openMultiStudentsModal(selectedClassroom)}
                      color="yellow"
                    />
                    <Button
                      iconLeft={<MdAddCircleOutline size={22} />}
                      label={t("add_new_student")}
                      disabled={!selectedClassroom}
                      onClick={() => openAddStudentModal(selectedClassroom)}
                      color="green"
                    />
                  </>
                }
              />
            </div>
          )}
        </>
      )}

      <AssignMultiStudentsModal ref={addMultiStudentsModalRef} onSubmit={handleAssignStudentsSuccess} />
      <AddStudentModal ref={addStudentModalRef} onSubmit={handleAddStudentsSuccess} />
    </>
  );
};

export default TeacherStudents;
